/**
 * CSV in and out of the Rules page, so a spreadsheet Shayna already keeps can be loaded
 * and the current rules taken back out to edit.
 *
 * The columns are the API's field names (plus `id`), so a file exported here imports
 * back unchanged. Headers are matched loosely — "Payer name", "payer_name" and
 * "payerName" are one column — because a hand-made sheet will not spell them our way.
 * The server checks every value; this only splits the text.
 */
import type { RuleTable } from './model'

/** Export / import columns per table, in order, by API field name. */
export const CSV_COLUMNS: Record<RuleTable, string[]> = {
  coinsurance_biller_rule: [
    'id',
    'matchField',
    'matchKind',
    'matchValue',
    'creditedDepartment',
    'priority',
    'effectiveFrom',
    'effectiveTo',
    'active',
    'note',
  ],
  coinsurance_payer: ['id', 'payerName', 'state', 'effectiveFrom', 'effectiveTo', 'weight', 'includeForHmo', 'includeForMedicare', 'active', 'note'],
  hard_payer: ['id', 'matchKind', 'payerName', 'state', 'effectiveFrom', 'effectiveTo', 'weight', 'active', 'reason'],
  payer_type_setting: ['id', 'payerType', 'setting', 'value', 'active', 'reason'],
}

/** Other spellings a hand-made sheet might use, by field, already squashed. */
const ALIASES: Record<RuleTable, Record<string, string[]>> = {
  coinsurance_biller_rule: {
    matchValue: ['match', 'value', 'payer', 'payor', 'payertype'],
    matchField: ['matchon', 'field'],
    creditedDepartment: ['department', 'dept', 'biller', 'creditedto'],
  },
  coinsurance_payer: {
    payerName: ['payer', 'payor', 'payorname', 'name'],
    includeForHmo: ['hmo', 'includehmo'],
    includeForMedicare: ['medicare', 'includemedicare'],
  },
  hard_payer: {
    payerName: ['payer', 'payor', 'payorname', 'name'],
    weight: ['payerweight', 'hardweight'],
  },
  payer_type_setting: {
    payerType: ['payertype', 'payortype', 'type'],
    value: ['days', 'limit', 'agelimit', 'maxagedays'],
  },
}

const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

/** RFC-4180: quoted fields, doubled quotes, CRLF or LF. A leading BOM is dropped. */
export function parseCSV(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  const s = text.replace(/^﻿/, '')
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!
    if (quoted) {
      if (ch === '"' && s[i + 1] === '"') {
        cell += '"'
        i++
      } else if (ch === '"') quoted = false
      else cell += ch
    } else if (ch === '"') quoted = true
    else if (ch === ',') {
      row.push(cell)
      cell = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else cell += ch
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell)
    rows.push(row)
  }
  // Blank lines (a trailing newline, a spacer row in a sheet) are not rows.
  return rows.filter((r) => r.some((c) => c.trim() !== ''))
}

/**
 * Rows as objects keyed by API field, for POST /api/rules/import. A column that maps to
 * no field is reported, not dropped: the server would refuse the file anyway, and
 * saying which header is the useful part.
 */
export function rowsForImport(
  table: RuleTable,
  text: string,
): { ok: true; rows: Record<string, string>[] } | { ok: false; error: string } {
  const [head, ...body] = parseCSV(text)
  if (!head) return { ok: false, error: 'The file is empty.' }
  // An older hard-payer export's extraWeight (weight − 1) is still read: the API turns it
  // into the weight (Shayna #163, 2026-10-07).
  const known = [...CSV_COLUMNS[table], ...(table === 'hard_payer' ? ['extraWeight'] : [])]
  const fields = head.map((h) => {
    const k = squash(h)
    return known.find((f) => squash(f) === k) ?? known.find((f) => ALIASES[table][f]?.includes(k)) ?? null
  })
  const unknown = head.filter((_, i) => fields[i] === null)
  if (unknown.length > 0) {
    return {
      ok: false,
      error: `Unrecognised column${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}. Expected: ${known.join(', ')}.`,
    }
  }
  if (body.length === 0) return { ok: false, error: 'The file has a header but no rows.' }
  return {
    ok: true,
    rows: body.map((r) => {
      const o: Record<string, string> = {}
      fields.forEach((f, i) => {
        o[f!] = (r[i] ?? '').trim()
      })
      return o
    }),
  }
}
